import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import type { RootState } from '../store';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Popover,
  Select,
  Stack,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import { centralDbRequest } from '../services/centralDbClient';

type DbSchema = { name: string };

type EasyflexSyncAction = 'default' | 'import' | 'syncPending' | 'full' | 'refresh';

type DbTable = {
  schema: string;
  name: string;
  type: string;
  columns: number;
  estimatedRows: number | null;
  easyflexSyncActions: EasyflexSyncAction[];
};

type DbColumn = {
  name: string;
  dataType: string;
  udtName: string;
  isNullable: boolean;
  default: string | null;
};

type TableDetail = {
  schema: string;
  table: string;
  columns: DbColumn[];
  primaryKey: string[];
  indexes: Array<{ name: string; definition: string }>;
  constraints: Array<{ name: string; type: string; columns: string[] }>;
  estimatedRows: number | null;
  totalRows: number | null;
  easyflexSyncActions: EasyflexSyncAction[];
};

type RowsPreview = {
  schema: string;
  table: string;
  columns: string[];
  limit: number;
  offset: number;
  rows: any[];
};

const jsonStringify = (v: any) => {
  try {
    return JSON.stringify(v, null, 2);
  } catch {
    return String(v);
  }
};

const formatValue = (v: any): string => {
  if (v === null || v === undefined) return '';
  if (typeof v === 'string') return v;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  return jsonStringify(v);
};

const DatabaseExplorerPage: React.FC = () => {
  const { accessToken } = useSelector((state: RootState) => (state as any).auth);

  const [status, setStatus] = useState<'idle' | 'loading' | 'done' | 'error'>('idle');
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<any>(null);

  const [schemas, setSchemas] = useState<DbSchema[]>([]);
  const [selectedSchema, setSelectedSchema] = useState<string>('public');
  const [tables, setTables] = useState<DbTable[]>([]);
  const [tableSearch, setTableSearch] = useState<string>('');
  const [selectedTableKey, setSelectedTableKey] = useState<string>('');

  const [tab, setTab] = useState<'rows' | 'meta'>('rows');
  const [tableDetail, setTableDetail] = useState<TableDetail | null>(null);
  const [rowsPreview, setRowsPreview] = useState<RowsPreview | null>(null);
  const [rowsLimit, setRowsLimit] = useState<number>(50);
  const [rowsOffset, setRowsOffset] = useState<number>(0);
  const [rowsSearch, setRowsSearch] = useState<string>('');
  const [exactCount, setExactCount] = useState<boolean>(false);
  const [hiddenColumns, setHiddenColumns] = useState<Set<string>>(new Set(['id']));
  const [columnMenuAnchor, setColumnMenuAnchor] = useState<HTMLElement | null>(null);

  const selected = useMemo(() => {
    if (!selectedTableKey) return null;
    const [schema, name] = selectedTableKey.split('.', 2);
    if (!schema || !name) return null;
    return { schema, table: name };
  }, [selectedTableKey]);

  const filteredTables = useMemo(() => {
    const q = tableSearch.trim().toLowerCase();
    if (!q) return tables;
    return tables.filter(t => `${t.schema}.${t.name}`.toLowerCase().includes(q));
  }, [tables, tableSearch]);

  const run = async (fn: () => Promise<any>) => {
    setStatus('loading');
    setError(null);
    setResult(null);
    try {
      const data = await fn();
      setResult(data);
      setStatus('done');
    } catch (e: any) {
      setStatus('error');
      setError(e?.message || 'Request failed');
    }
  };

  const loadSchemas = async () => {
    const res = await centralDbRequest({ method: 'GET', path: '/admin/db/schemas', token: accessToken });
    if (!res.ok) throw new Error(typeof res.data === 'string' ? res.data : `HTTP ${res.status}`);
    setSchemas(Array.isArray(res.data?.items) ? res.data.items : []);
  };

  const loadTables = async (schema: string) => {
    const res = await centralDbRequest({ method: 'GET', path: '/admin/db/tables', token: accessToken, query: { schema } });
    if (!res.ok) throw new Error(typeof res.data === 'string' ? res.data : `HTTP ${res.status}`);
    setTables(Array.isArray(res.data?.items) ? res.data.items : []);
  };

  const loadTableDetail = async (schema: string, table: string, count: boolean) => {
    const res = await centralDbRequest({
      method: 'GET',
      path: `/admin/db/tables/${schema}/${table}`,
      token: accessToken,
      query: { exactCount: count },
    });
    if (!res.ok) throw new Error(typeof res.data === 'string' ? res.data : `HTTP ${res.status}`);
    setTableDetail(res.data as TableDetail);
  };

  const loadRows = async (schema: string, table: string, limit: number, offset: number, search?: string) => {
    const res = await centralDbRequest({
      method: 'GET',
      path: `/admin/db/tables/${schema}/${table}/rows`,
      token: accessToken,
      query: { limit, offset, ...(search ? { search } : {}) },
    });
    if (!res.ok) throw new Error(typeof res.data === 'string' ? res.data : `HTTP ${res.status}`);
    setRowsPreview(res.data as RowsPreview);
  };

  useEffect(() => {
    run(async () => {
      await loadSchemas();
      await loadTables(selectedSchema);
      return { ok: true };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    run(async () => {
      await loadTables(selectedSchema);
      setSelectedTableKey('');
      setTableDetail(null);
      setRowsPreview(null);
      setRowsOffset(0);
      return { ok: true };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSchema]);

  useEffect(() => {
    if (!selected) return;
    setHiddenColumns(new Set(['id']));
    setRowsSearch(''); // Reset search when selecting a new table
    run(async () => {
      await Promise.all([
        loadTableDetail(selected.schema, selected.table, exactCount),
        loadRows(selected.schema, selected.table, rowsLimit, 0), // Reset offset too
      ]);
      return { ok: true };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTableKey]);

  useEffect(() => {
    if (!selected) return;
    run(async () => {
      await loadRows(selected.schema, selected.table, rowsLimit, rowsOffset, rowsSearch || undefined);
      return { ok: true };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowsLimit, rowsOffset]);

  useEffect(() => {
    if (!selected) return;
    run(async () => {
      await loadTableDetail(selected.schema, selected.table, exactCount);
      return { ok: true };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exactCount]);

  const triggerSync = async (action: EasyflexSyncAction, payload?: any) => {
    if (!selected) throw new Error('Select a table first');
    const res = await centralDbRequest({
      method: 'POST',
      path: `/admin/db/tables/${selected.schema}/${selected.table}/sync/easyflex`,
      token: accessToken,
      body: { action, payload },
    });
    if (!res.ok) throw new Error(typeof res.data === 'string' ? res.data : `HTTP ${res.status}`);
    return res.data;
  };

  const selectedTable = useMemo(() => tables.find(t => `${t.schema}.${t.name}` === selectedTableKey) || null, [tables, selectedTableKey]);
  const easyflexActions = tableDetail?.easyflexSyncActions?.length ? tableDetail.easyflexSyncActions : selectedTable?.easyflexSyncActions || [];

  const canPrev = rowsOffset > 0;
  const canNext = (rowsPreview?.rows?.length || 0) >= rowsLimit;

  const visibleColumns = useMemo(() => {
    if (!rowsPreview?.columns) return [];
    return rowsPreview.columns.filter(c => !hiddenColumns.has(c));
  }, [rowsPreview?.columns, hiddenColumns]);

  const toggleColumn = (col: string) => {
    setHiddenColumns(prev => {
      const next = new Set(prev);
      if (next.has(col)) {
        next.delete(col);
      } else {
        next.add(col);
      }
      return next;
    });
  };

  const showAllColumns = () => setHiddenColumns(new Set());
  const hideAllColumns = () => setHiddenColumns(new Set(rowsPreview?.columns || []));

  return (
    <Stack spacing={2} sx={{ height: '100%', minHeight: 0, overflow: 'hidden' }}>
      <Typography variant="h5" fontWeight={800} sx={{ flexShrink: 0 }}>
        DB Explorer
      </Typography>

      <Card variant="outlined" sx={{ flexShrink: 0 }}>
        <CardContent>
          <Stack spacing={1.5}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }}>
              <FormControl fullWidth>
                <InputLabel id="schema-label" children="Schema" />
                <Select
                  labelId="schema-label"
                  label="Schema"
                  value={selectedSchema}
                  onChange={e => setSelectedSchema(String(e.target.value))}
                >
                  {schemas.map(s => (
                    <MenuItem key={s.name} value={s.name}>
                      {s.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                label="Search tables"
                value={tableSearch}
                onChange={e => setTableSearch(e.target.value)}
                fullWidth
              />

              <Button
                variant="outlined"
                onClick={() => run(async () => (await loadTables(selectedSchema), { ok: true }))}
                disabled={status === 'loading'}
              >
                Refresh
              </Button>
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      <Card variant="outlined" sx={{ flexShrink: 0 }}>
        <CardContent>
          <Stack spacing={1}>
            <Typography variant="subtitle1" fontWeight={700}>
              Tables ({filteredTables.length})
            </Typography>
            <Box sx={{ maxHeight: 200, overflow: 'auto', border: '1px solid rgba(0,0,0,0.08)', borderRadius: 1 }}>
              <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ p: 1 }}>
                {filteredTables.map(t => {
                  const key = `${t.schema}.${t.name}`;
                  const isSelected = selectedTableKey === key;
                  return (
                    <Chip
                      key={key}
                      label={t.name}
                      size="small"
                      variant={isSelected ? 'filled' : 'outlined'}
                      color={isSelected ? 'primary' : 'default'}
                      onClick={() => {
                        setSelectedTableKey(key);
                        setRowsOffset(0);
                      }}
                      sx={{ cursor: 'pointer' }}
                    />
                  );
                })}
                {!filteredTables.length ? (
                  <Typography variant="body2" color="text.secondary">
                    No tables found.
                  </Typography>
                ) : null}
              </Stack>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      <Card variant="outlined" sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <CardContent sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}>
            <Stack spacing={1.5} sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ md: 'center' }} sx={{ flexShrink: 0 }}>
                <Typography variant="subtitle1" fontWeight={700} sx={{ flexGrow: 1 }}>
                  {selected ? `${selected.schema}.${selected.table}` : 'Select a table'}
                </Typography>

                {selected ? (
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {easyflexActions.includes('import') ? (
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={status === 'loading'}
                        onClick={() => run(async () => triggerSync('import'))}
                      >
                        Easyflex import
                      </Button>
                    ) : null}
                    {easyflexActions.includes('syncPending') ? (
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={status === 'loading'}
                        onClick={() => run(async () => triggerSync('syncPending'))}
                      >
                        Easyflex sync pending
                      </Button>
                    ) : null}
                    {easyflexActions.includes('refresh') ? (
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={status === 'loading'}
                        onClick={() => run(async () => triggerSync('refresh'))}
                      >
                        Easyflex refresh
                      </Button>
                    ) : null}
                    {easyflexActions.includes('full') ? (
                      <Button
                        size="small"
                        variant="contained"
                        disabled={status === 'loading'}
                        onClick={() => run(async () => triggerSync('full'))}
                      >
                        Easyflex full
                      </Button>
                    ) : null}
                  </Stack>
                ) : null}
              </Stack>

              <Divider sx={{ flexShrink: 0 }} />

              <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ minHeight: 0, flexShrink: 0 }}>
                <Tab value="rows" label="Rows" />
                <Tab value="meta" label="Metadata" />
              </Tabs>

              {tab === 'rows' ? (
                <Stack spacing={1.5} sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
                  {/* Table info bar */}
                  {selected && (
                    <Stack direction="row" spacing={2} alignItems="center" sx={{ flexShrink: 0, px: 1, py: 0.5, bgcolor: 'action.hover', borderRadius: 1 }}>
                      <Typography variant="body2" fontWeight={600}>
                        {tableDetail?.estimatedRows != null
                          ? `~${tableDetail.estimatedRows.toLocaleString()} rows`
                          : selectedTable?.estimatedRows != null
                          ? `~${selectedTable.estimatedRows.toLocaleString()} rows`
                          : '— rows'}
                      </Typography>
                      {tableDetail?.totalRows != null && (
                        <Chip size="small" label={`exact: ${tableDetail.totalRows.toLocaleString()}`} color="info" variant="outlined" />
                      )}
                      <Typography variant="body2" color="text.secondary">
                        {tableDetail?.columns?.length ?? selectedTable?.columns ?? '—'} columns
                      </Typography>
                      <Box sx={{ flexGrow: 1 }} />
                      <Typography variant="caption" color="text.secondary">
                        showing {rowsOffset + 1}–{rowsOffset + (rowsPreview?.rows?.length ?? 0)}
                      </Typography>
                    </Stack>
                  )}

                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }} sx={{ flexShrink: 0 }}>
                    <TextField
                      label="Search"
                      placeholder="Search all text columns..."
                      value={rowsSearch}
                      onChange={e => setRowsSearch(e.target.value)}
                      onKeyDown={e => {
                        if (e.key === 'Enter' && selected) {
                          setRowsOffset(0);
                          run(async () => loadRows(selected.schema, selected.table, rowsLimit, 0, rowsSearch || undefined));
                        }
                      }}
                      size="small"
                      sx={{ minWidth: 200 }}
                    />
                    <Button
                      variant="contained"
                      size="small"
                      disabled={status === 'loading' || !selected}
                      onClick={() => {
                        if (selected) {
                          setRowsOffset(0);
                          run(async () => loadRows(selected.schema, selected.table, rowsLimit, 0, rowsSearch || undefined));
                        }
                      }}
                    >
                      Search
                    </Button>
                    <TextField
                      label="Limit"
                      type="number"
                      value={rowsLimit}
                      onChange={e => setRowsLimit(Math.max(1, Math.min(200, Number(e.target.value) || 50)))}
                      size="small"
                      sx={{ width: 100 }}
                    />
                    <Button
                      variant="outlined"
                      size="small"
                      disabled={status === 'loading' || !selected}
                      onClick={() => selected && run(async () => loadRows(selected.schema, selected.table, rowsLimit, rowsOffset, rowsSearch || undefined))}
                    >
                      Reload
                    </Button>
                    {rowsPreview?.columns?.length ? (
                      <>
                        <IconButton
                          size="small"
                          onClick={e => setColumnMenuAnchor(e.currentTarget)}
                          title="Select columns"
                        >
                          <SettingsIcon fontSize="small" />
                        </IconButton>
                        <Typography variant="caption" color="text.secondary">
                          {visibleColumns.length}/{rowsPreview.columns.length} cols
                        </Typography>
                        <Popover
                          open={Boolean(columnMenuAnchor)}
                          anchorEl={columnMenuAnchor}
                          onClose={() => setColumnMenuAnchor(null)}
                          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                          disableScrollLock
                        >
                          <Box sx={{ p: 2, maxHeight: 400, overflow: 'auto', minWidth: 200 }}>
                            <Stack spacing={0.5}>
                              <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                                <Button size="small" onClick={showAllColumns}>Show all</Button>
                                <Button size="small" onClick={hideAllColumns}>Hide all</Button>
                              </Stack>
                              {rowsPreview.columns.map(col => (
                                <FormControlLabel
                                  key={col}
                                  control={
                                    <Checkbox
                                      size="small"
                                      checked={!hiddenColumns.has(col)}
                                      onChange={() => toggleColumn(col)}
                                    />
                                  }
                                  label={<Typography variant="body2">{col}</Typography>}
                                  sx={{ m: 0 }}
                                />
                              ))}
                            </Stack>
                          </Box>
                        </Popover>
                      </>
                    ) : null}
                    <Box sx={{ flexGrow: 1 }} />
                    <Button
                      variant="outlined"
                      size="small"
                      disabled={!selected || status === 'loading' || !canPrev}
                      onClick={() => setRowsOffset(Math.max(0, rowsOffset - rowsLimit))}
                    >
                      Prev
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      disabled={!selected || status === 'loading' || !canNext}
                      onClick={() => setRowsOffset(rowsOffset + rowsLimit)}
                    >
                      Next
                    </Button>
                    <Typography variant="caption" color="text.secondary">
                      offset {rowsOffset}
                    </Typography>
                  </Stack>

                  {rowsPreview ? (
                    <TableContainer sx={{ flex: 1, minHeight: 0, border: '1px solid rgba(0,0,0,0.08)', borderRadius: 1, overflow: 'auto' }}>
                      <Table stickyHeader size="small" sx={{ tableLayout: 'auto' }}>
                        <TableHead>
                          <TableRow>
                            {visibleColumns.map(c => (
                              <React.Fragment key={c}>
                                <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
                                  {c}
                                </TableCell>
                              </React.Fragment>
                            ))}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {rowsPreview.rows.map((r, idx) => (
                            <TableRow key={idx} hover>
                              {visibleColumns.map(c => (
                                <React.Fragment key={c}>
                                  <TableCell sx={{ maxWidth: 400, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {formatValue((r as any)?.[c])}
                                  </TableCell>
                                </React.Fragment>
                              ))}
                            </TableRow>
                          ))}
                          {!rowsPreview.rows?.length ? (
                            <TableRow>
                              <TableCell {...({ colSpan: visibleColumns.length || 1 } as any)}>
                                <Typography variant="body2" color="text.secondary">
                                  No rows returned.
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ) : null}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      Select a table to preview rows.
                    </Typography>
                  )}
                </Stack>
              ) : null}

              {tab === 'meta' ? (
                <Stack spacing={1.5} sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
                  {tableDetail ? (
                    <>
                      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }} sx={{ flexShrink: 0 }}>
                        <Typography variant="body2" color="text.secondary">
                          estimated rows: {tableDetail.estimatedRows ?? '—'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          exact rows: {tableDetail.totalRows ?? '—'}
                        </Typography>
                        <Box sx={{ flexGrow: 1 }} />
                        <Button
                          size="small"
                          variant={exactCount ? 'contained' : 'outlined'}
                          disabled={status === 'loading' || !selected}
                          onClick={() => setExactCount(v => !v)}
                        >
                          {exactCount ? 'Exact count on' : 'Exact count off'}
                        </Button>
                      </Stack>

                      <Typography variant="subtitle2" fontWeight={700} sx={{ flexShrink: 0 }}>
                        Columns ({tableDetail.columns.length})
                      </Typography>
                      <TableContainer sx={{ flex: 1, minHeight: 0, border: '1px solid rgba(0,0,0,0.08)', borderRadius: 1, overflow: 'auto' }}>
                        <Table stickyHeader size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
                              <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                              <TableCell sx={{ fontWeight: 700 }}>Nullable</TableCell>
                              <TableCell sx={{ fontWeight: 700 }}>Default</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {tableDetail.columns.map(col => (
                              <TableRow key={col.name} hover>
                                <TableCell sx={{ whiteSpace: 'nowrap' }}>{col.name}</TableCell>
                                <TableCell sx={{ whiteSpace: 'nowrap' }}>{col.dataType || col.udtName}</TableCell>
                                <TableCell>{col.isNullable ? 'yes' : 'no'}</TableCell>
                                <TableCell sx={{ maxWidth: 420, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {col.default || ''}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>

                      <Typography variant="subtitle2" fontWeight={700} sx={{ flexShrink: 0 }}>
                        Primary key
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0 }}>
                        {tableDetail.primaryKey?.length ? tableDetail.primaryKey.join(', ') : '—'}
                      </Typography>
                    </>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      Select a table to see metadata.
                    </Typography>
                  )}
                </Stack>
              ) : null}
            </Stack>
          </CardContent>
        </Card>

      {status === 'error' ? <Alert severity="error" sx={{ flexShrink: 0 }}>{error}</Alert> : null}
      {status === 'done' ? <Alert severity="success" sx={{ flexShrink: 0 }}>Done.</Alert> : null}
      {result ? (
        <Card variant="outlined" sx={{ flexShrink: 0, maxHeight: 200, overflow: 'auto' }}>
          <CardContent>
            <Typography variant="subtitle2" color="text.secondary">
              Last response
            </Typography>
            <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{jsonStringify(result)}</pre>
          </CardContent>
        </Card>
      ) : null}
    </Stack>
  );
};

export default DatabaseExplorerPage;

